import { useSeo } from '../lib/seo';
/**
 * /about — "Who We Are" trust & credibility page.
 *
 * The brief: establish Leicestershire Promotions as the longest-established
 * independent DMO behind the site, with 30+ years promoting the county. The
 * page is built to do two jobs at once:
 *   1. Reassure visitors that recommendations come from real local expertise
 *      (not a generic travel aggregator, not a politically-influenced source)
 *   2. Earn SEO authority for the cluster of "Leicester tourism /
 *      Leicestershire tourism / things to do in Leicestershire" queries via
 *      naturally-integrated copy + Organization JSON-LD
 *
 * Layout: contextual hero · four trust pillars · ~400-word story · trust
 * signals strip · CTAs back into the rest of the site.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight,
  BadgeCheck,
  CalendarCheck,
  Check,
  MapPin,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';

const PILLARS = [
  {
    icon: CalendarCheck,
    title: 'Established 1993',
    body: 'Three decades championing Leicester and Leicestershire — one of the UK\'s longest-established Destination Management Organisations.',
  },
  {
    icon: ShieldCheck,
    title: 'Independent & Not-for-Profit',
    body: 'Focused on the visitor economy and the people who live here — not on shareholders or political cycles.',
  },
  {
    icon: MapPin,
    title: 'Local Experts',
    body: 'Our team lives, works and weekends in Leicestershire. Every tip is one we\'d give a friend.',
  },
];

// Kept short and factual — claims about external endorsements removed.
const TRUST_SIGNALS = [
  'Established 1993',
  'Independent · Not-for-Profit',
  '30+ Years Destination Expertise',
  'Local Leicestershire Team',
];

export default function AboutPage() {
  useSeo(
    'About Go Leicestershire — Who We Are',
    'The story behind Go Leicestershire: local knowledge, honest guides and a mission to champion the county’s venues, towns and events.'
  );
  return (
    <div data-testid="about-page">
      <Helmet>
        <title>About Leicestershire Promotions · The Leicester &amp; Leicestershire visitor guide</title>
        <meta
          name="description"
          content="GoLeicestershire is run by Leicestershire Promotions — established 1993, one of the UK's longest-running independent Destination Management Organisations. Three decades of local Leicester and Leicestershire tourism knowledge."
        />
        <meta name="robots" content="index,follow" />
        <meta property="og:title" content="About Leicestershire Promotions — 30+ years of Leicestershire tourism" />
        <meta property="og:description" content="Independent, not-for-profit, locally-led. The team behind GoLeicestershire has been championing Leicester and Leicestershire since 1993." />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Leicestershire Promotions Ltd',
          alternateName: ['GoLeicestershire', 'Leicestershire Promotions'],
          url: 'https://www.goleicestershire.com/about',
          logo: 'https://www.goleicestershire.com/logo.png',
          foundingDate: '1993',
          description: 'Leicestershire Promotions is one of the UK\'s longest-established Destination Management Organisations, established 1993. An independent, not-for-profit organisation providing Leicester and Leicestershire tourism information.',
          areaServed: { '@type': 'AdministrativeArea', name: 'Leicestershire, England' },
          knowsAbout: [
            'Leicester tourism', 'Leicestershire tourism', 'Visit Leicestershire',
            'Things to do in Leicestershire', 'Days out in Leicestershire',
            'Leicester attractions', 'Family days out in Leicestershire',
          ],
          sameAs: ['https://www.stayplayexplore.co.uk/'],
        })}</script>
      </Helmet>

      {/* HERO */}
      <section className="bg-stone border-b border-charcoal/10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="label text-heritage mb-4" data-testid="about-eyebrow">Who we are</div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-6">
                Three decades championing <em className="not-italic text-heritage">Leicester &amp; Leicestershire.</em>
              </h1>
              <p className="text-lg md:text-xl text-charcoal/75 max-w-2xl leading-relaxed">
                Behind GoLeicestershire is Leicestershire Promotions — one of the UK&apos;s longest-established
                Destination Management Organisations. Established in 1993, we&apos;re an independent,
                not-for-profit team of Leicestershire people whose job — and passion — is helping
                visitors and locals discover the best of this county.
              </p>
            </div>
            <div className="lg:col-span-4">
              <div className="bg-white rounded-3xl p-6 md:p-7 border border-charcoal/10" data-testid="about-stat-card">
                <div className="label text-mustard mb-3">At a glance</div>
                <dl className="space-y-4">
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-charcoal/50">Founded</dt>
                    <dd className="font-display text-3xl text-charcoal">1993</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-charcoal/50">Years of expertise</dt>
                    <dd className="font-display text-3xl text-charcoal">30+</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-charcoal/50">Structure</dt>
                    <dd className="font-display text-xl text-charcoal">Independent · Not-for-Profit</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THREE TRUST PILLARS */}
      <section className="bg-base">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-20">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  data-testid={`about-pillar-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className="bg-white border border-charcoal/10 rounded-3xl p-6 lg:p-7 hover:border-heritage/40 hover:shadow-soft transition-all"
                >
                  <div className="w-11 h-11 rounded-2xl bg-heritage/10 text-heritage flex items-center justify-center mb-5">
                    <Icon size={20} />
                  </div>
                  <div className="font-display text-xl leading-tight mb-2">{p.title}</div>
                  <p className="text-charcoal/70 text-sm leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MAIN STORY — ~400 words, SEO-aware */}
      <section className="bg-stone border-y border-charcoal/10">
        <div className="max-w-[920px] mx-auto px-6 lg:px-12 py-20 lg:py-24">
          <div className="label text-heritage mb-4">Our story</div>
          <h2 className="font-display text-3xl lg:text-5xl leading-tight mb-10">
            Built in Leicestershire, for Leicestershire.
          </h2>

          <div className="prose prose-lg max-w-none text-charcoal/85 leading-relaxed [&_p]:mb-5 [&_h3]:font-display [&_h3]:text-2xl [&_h3]:mt-10 [&_h3]:mb-3 [&_strong]:text-charcoal">
            <p>
              Leicestershire Promotions was founded in <strong>1993</strong> with a single, enduring
              purpose: to put Leicester and Leicestershire on the map — and to help every visitor,
              and every resident, find the best of it. Three decades later, that purpose hasn&apos;t
              changed. We&apos;re one of the UK&apos;s longest-established Destination Management
              Organisations (also known as a tourist board) — independent, not-for-profit, and
              focused on the visitor economy and the people who live and work here.
            </p>

            <p>
              <strong>GoLeicestershire</strong> is our online front door. It&apos;s where we bring
              together everything we know about{' '}
              <strong>Leicestershire tourism</strong> and <strong>Leicester tourism</strong>:
              the castles, the market towns, the National Forest, the city centre with its
              Roman walls and award-winning food scene, the country pubs, the family farms,
              the gardens, and the festivals that fill the calendar from spring through Christmas.
              It&apos;s a <strong>Leicester visitor guide</strong> and a{' '}
              <strong>Leicestershire visitor guide</strong> rolled into one — built by people who
              actually go to these places themselves.
            </p>

            <h3>Local expertise, written by locals</h3>
            <p>
              Our team lives in Leicestershire. We do the school runs here, eat in the
              restaurants on this site, take our families to the{' '}
              <Link to="/explore" className="text-heritage hover:underline">attractions</Link>{' '}
              we recommend, and spend our weekends following the same{' '}
              <Link to="/half-days" className="text-heritage hover:underline">half-day loops</Link>{' '}
              and{' '}
              <Link to="/itinerary" className="text-heritage hover:underline">itineraries</Link>{' '}
              we publish. When you read about <em>family days out in Leicestershire</em> or
              <em> things to do in Leicester</em> on this site, you&apos;re reading first-hand
              knowledge — not a re-tread of a press release. We update our listings constantly,
              cross-check opening times with venues, and only feature places we&apos;d send a friend.
            </p>

            <h3>Why this matters to you</h3>
            <p>
              When you&apos;re planning a day out, choosing where to take the kids, or working out
              <em> places to visit in Leicestershire</em> for the weekend, you deserve accurate,
              up-to-date, locally-curated information. That&apos;s what we&apos;re here for —
              whether you&apos;re looking for <em>events in Leicester and Leicestershire</em>,
              the best <em> Leicester attractions</em>, or simply trustworthy{' '}
              <em>tourist information for Leicestershire</em>.
            </p>
          </div>
        </div>
      </section>

      {/* QUIET CLOSER — light trust signals as inline ticks + one understated CTA.
          Replaces the earlier dark trust-strip + 3-CTA block which felt too defensive. */}
      <section className="bg-base" data-testid="about-trust-signals">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-14 md:py-16">
          <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-charcoal/70">
            {TRUST_SIGNALS.map((s) => (
              <div
                key={s}
                className="inline-flex items-center gap-2 text-sm"
                data-testid={`trust-signal-${s.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <Check size={14} className="text-heritage shrink-0" strokeWidth={2.5} />
                <span>{s}</span>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
            <Link
              to="/explore"
              data-testid="about-cta-explore"
              className="inline-flex items-center gap-2 text-heritage hover:text-charcoal font-semibold"
            >
              Start exploring the county <ArrowRight size={14} />
            </Link>
            <span className="hidden sm:inline text-charcoal/30">·</span>
            <Link
              to="/plan"
              data-testid="about-cta-plan"
              className="inline-flex items-center gap-2 text-heritage hover:text-charcoal font-semibold"
            >
              Plan a day out <ArrowRight size={14} />
            </Link>
            <span className="hidden sm:inline text-charcoal/30">·</span>
            <Link
              to="/services"
              data-testid="about-cta-services"
              className="inline-flex items-center gap-2 text-heritage hover:text-charcoal font-semibold"
            >
              Working in tourism? See our services <ArrowRight size={14} />
            </Link>
          </div>

          <div className="mt-10 pt-6 border-t border-charcoal/10 flex items-start gap-3 justify-center text-xs text-charcoal/55">
            <BadgeCheck size={14} className="text-heritage shrink-0 mt-0.5" />
            <div>
              <strong className="text-charcoal/75">Leicestershire Promotions Ltd</strong> · Independent Destination Management Organisation · Registered in England ·{' '}
              <a href="mailto:info@goleicestershire.com" className="text-heritage hover:underline">info@goleicestershire.com</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// Keep a named export for the homepage teaser (a smaller, more concise
// version of the trust pillars that links into this full page).
export function AboutTeaser() {
  return (
    <section className="bg-stone border-y border-charcoal/10" data-testid="home-about-teaser">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-12 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-6">
            <div className="label text-heritage mb-3" data-testid="home-about-teaser-eyebrow">Who&apos;s behind this site</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight mb-5">
              Local know-how, <em className="not-italic text-heritage">since 1993.</em>
            </h2>
            <p className="text-charcoal/75 text-base md:text-lg leading-relaxed mb-6 max-w-xl">
              GoLeicestershire is run by Leicestershire Promotions — an independent,
              not-for-profit Destination Management Organisation. Three decades of local
              knowledge, written by people who live here.
            </p>
            <Link
              to="/about"
              data-testid="home-about-teaser-cta"
              className="inline-flex items-center gap-2 text-heritage hover:text-charcoal font-semibold text-sm"
            >
              About Leicestershire Promotions <ArrowRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { icon: CalendarCheck, label: 'Established 1993' },
                { icon: ShieldCheck, label: 'Independent · Not-for-Profit' },
                { icon: MapPin, label: 'Local Leicestershire team' },
                { icon: Sparkles, label: '30+ years of local know-how' },
              ].map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="bg-white border border-charcoal/10 rounded-2xl p-4 flex items-start gap-3"
                  data-testid={`home-about-pill-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  <span className="w-8 h-8 rounded-xl bg-heritage/10 text-heritage flex items-center justify-center shrink-0"><Icon size={15}/></span>
                  <div className="text-sm font-semibold text-charcoal/85 leading-snug">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// End of file.
